import { useCallback } from 'react';
import { createSortContext, universalSort } from '../utils/sortingLogic';
import { getCableDisplayName, getEarthCableDisplayName, analyzeAndGroupCables } from '../utils/cableLogic';

export const useGroupedCables = (cables, listSortMode, settings) => {

  const groupCablesByMode = useCallback(() => {
    // ==================== PARTE COMUNE - ORDINAMENTO E RAGGRUPPAMENTO BASE ====================

    const context = createSortContext(settings.lingua);
    const sortedCables = universalSort(
      cables.map((cable, idx) => ({
        ...cable,
        originalIndex: cable.originalIndex !== undefined ? cable.originalIndex : idx,
        siglatura: settings.marcaturePerCollegamento
          ? getCableDisplayName(cable, settings.lingua, settings)
          : getEarthCableDisplayName(cable, settings.lingua)
      })),
      listSortMode,
      context
    );

    // Genera chiave di raggruppamento
    const getGroupingKey = (cable) => {
      // PRIORITÀ 1: Gestione guaine - i cavi con la stessa guaina vanno sempre insieme
      // MA NON per modalità siglatura/colore/sezione/colore+sezione (dove non devono raggrupparsi)
      const skipSheathGrouping = listSortMode === 'siglatura' || listSortMode === 'colore' || listSortMode === 'sezione' || listSortMode === 'coloreSezione';
      if (cable.guaina && !cable.poli && !skipSheathGrouping) {
        return `sheath_${cable.guaina}`;
      }

      // PRIORITÀ 2: Gestione speciale per cavi multipolari
      if (cable.poli && Array.isArray(cable.poli)) {
        if (cable.nomeCavo && cable.nomeCavo.trim() !== '') {
          return cable.nomeCavo;
        } else {
          return `[Cavo Multipolare #${cable.originalIndex + 1}]`;
        }
      }

      // PRIORITÀ 3: Cavi standard
      // Per modalità colore/sezione/colore+sezione: ogni cavo ha una chiave unica (nessun raggruppamento)
      if (listSortMode === 'colore' || listSortMode === 'sezione' || listSortMode === 'coloreSezione') {
        return `cable_${cable.originalIndex}`;
      }

      if (!settings.marcaturePerCollegamento) {
        return cable.siglatura || `[Cavo #${cable.originalIndex + 1}]`;
      } else {
        return `cable_${cable.originalIndex}`;
      }
    };

    // Raggruppa usando la chiave di raggruppamento
    const groups = {};

    sortedCables.forEach((cable) => {
      const groupKey = getGroupingKey(cable);
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(cable);
    });

    // ==================== GESTIONE SPECIFICA PER MODALITÀ ====================

    if (listSortMode === 'numerazione') {
      // ✅ MODALITÀ NUMERAZIONE - con gestione gruppi forzati

      // Prima controlla se ci sono effettivamente cavi con gruppi
      const cablesWithGroups = cables.filter(cable => cable.gruppo && cable.gruppo.trim() !== '');
      const hasGroups = cablesWithGroups.length > 0;

      if (hasGroups) {
        // ✅ MODALITÀ NUMERAZIONE CON GRUPPI FORZATI

        const cablesWithIndex = cables.map((cable, index) => ({
          ...cable,
          originalIndex: cable.originalIndex !== undefined ? cable.originalIndex : index,
          siglatura: settings.marcaturePerCollegamento
            ? getCableDisplayName(cable, settings.lingua, settings)
            : getEarthCableDisplayName(cable, settings.lingua)
        }));

        const sortedCables = cablesWithIndex.sort((a, b) => a.originalIndex - b.originalIndex);
        const finalGroups = {};

        // ========== SEPARAZIONE MODALITÀ ==========

        if (settings.marcaturePerCollegamento) {
          // 🔵 MODALITÀ COLLEGAMENTO - Stessa logica della modalità siglatura

          let currentGroup = null;
          let groupCounter = 0;
          let ungroupedBuffer = [];

          const flushUngroupedBuffer = () => {
            if (ungroupedBuffer.length === 0) return;
            const subGroups = analyzeAndGroupCables(ungroupedBuffer, settings);
            subGroups.forEach((subGroup, subGroupIndex) => {
              if (subGroup.cables.length === 1) {
                const only = subGroup.cables[0];
                finalGroups[`single_${only.originalIndex}`] = [only];
              } else {
                const key = `multi_${subGroup.cables.map(c => c.originalIndex).join('_')}_${subGroupIndex}`;
                finalGroups[key] = subGroup.cables;
              }
            });
            ungroupedBuffer = [];
          };

          const finalizeCurrentGroup = () => {
            if (currentGroup && currentGroup.type === 'forced_group') {
              currentGroup.subGroups = analyzeAndGroupCables(currentGroup.allCables, settings);
              const key = `forced_group_${currentGroup.groupName}_${groupCounter}`;
              finalGroups[key] = currentGroup;
              groupCounter++;
            }
            currentGroup = null;
          };

          sortedCables.forEach((cable) => {
            const hasGroup = cable.gruppo && cable.gruppo.trim() !== '';

            if (hasGroup) {
              flushUngroupedBuffer();

              const groupName = cable.gruppo.trim();
              const canJoinCurrentGroup = currentGroup &&
                currentGroup.groupName === groupName &&
                currentGroup.type === 'forced_group';

              if (canJoinCurrentGroup) {
                currentGroup.allCables.push(cable);
                currentGroup.totalCables++;
              } else {
                if (currentGroup) finalizeCurrentGroup();
                currentGroup = {
                  type: 'forced_group',
                  groupName: groupName,
                  allCables: [cable],
                  totalCables: 1,
                  subGroups: []
                };
              }
            } else {
              ungroupedBuffer.push(cable);
            }
          });

          if (currentGroup) finalizeCurrentGroup();
          flushUngroupedBuffer();

        } else {
          // 🟢 MODALITÀ SIGLATURA 

          let currentGroup = null;
          let groupCounter = 0;
          let ungroupedBuffer = [];

          const flushUngroupedBuffer = () => {
            if (ungroupedBuffer.length === 0) return;
            const subGroups = analyzeAndGroupCables(ungroupedBuffer, settings);
            subGroups.forEach((subGroup, subGroupIndex) => {
              if (subGroup.cables.length === 1) {
                const only = subGroup.cables[0];
                finalGroups[`single_${only.originalIndex}`] = [only];
              } else {
                const key = `multi_${subGroup.cables.map(c => c.originalIndex).join('_')}_${subGroupIndex}`;
                finalGroups[key] = subGroup.cables;
              }
            });
            ungroupedBuffer = [];
          };

          const finalizeCurrentGroup = () => {
            if (currentGroup && currentGroup.type === 'forced_group') {
              currentGroup.subGroups = analyzeAndGroupCables(currentGroup.allCables, settings);
              const key = `forced_group_${currentGroup.groupName}_${groupCounter}`;
              finalGroups[key] = currentGroup;
              groupCounter++;
            }
            currentGroup = null;
          };

          sortedCables.forEach((cable) => {
            const hasGroup = cable.gruppo && cable.gruppo.trim() !== '';

            if (hasGroup) {
              flushUngroupedBuffer();

              const groupName = cable.gruppo.trim();
              const canJoinCurrentGroup = currentGroup &&
                currentGroup.groupName === groupName &&
                currentGroup.type === 'forced_group';

              if (canJoinCurrentGroup) {
                currentGroup.allCables.push(cable);
                currentGroup.totalCables++;
              } else {
                if (currentGroup) finalizeCurrentGroup();
                currentGroup = {
                  type: 'forced_group',
                  groupName: groupName,
                  allCables: [cable],
                  totalCables: 1,
                  subGroups: []
                };
              }
            } else {
              ungroupedBuffer.push(cable);
            }
          });

          if (currentGroup) finalizeCurrentGroup();
          flushUngroupedBuffer();
        }

        return finalGroups;
      }

      // ✅ MODALITÀ NUMERAZIONE SENZA GRUPPI - gestione consecutività normale
      const finalGroups = {};

      // Modalità collegamento - applica analyzeAndGroupCables
      if (settings.marcaturePerCollegamento) {
        const allByOriginalIndex = cables
          .map((cable, index) => ({
            ...cable,
            originalIndex: index,
            groupKey: getGroupingKey({ ...cable, originalIndex: index })
          }))
          .sort((a, b) => a.originalIndex - b.originalIndex);

        const subGroups = analyzeAndGroupCables(allByOriginalIndex, settings);
        subGroups.forEach((subGroup, subGroupIndex) => {
          const key = subGroup.cables.length === 1
            ? `single_${subGroup.cables[0].originalIndex}`
            : `multi_${subGroup.cables.map(c => c.originalIndex).join('_')}_${subGroupIndex}`;
          finalGroups[key] = subGroup.cables;
        });
        return finalGroups;
      }

      // Logica esistente per modalità normale con consecutività
      Object.entries(groups).forEach(([groupKey, cablesInGroup]) => {
        // ✅ GESTIONE SPECIALE: I gruppi guaina NON devono essere divisi
        if (groupKey.startsWith('sheath_')) {
          // Mantieni il gruppo guaina intero
          finalGroups[groupKey] = cablesInGroup;
          return;
        }

        if (cablesInGroup.length === 1) {
          const displayKey = `single_${cablesInGroup[0].originalIndex}`;
          finalGroups[displayKey] = cablesInGroup;
          return;
        }

        // ✅ CORREZIONE: Usa analyzeAndGroupCables per verificare i collegamenti
        const subGroups = analyzeAndGroupCables(cablesInGroup, settings);
        subGroups.forEach((subGroup, subGroupIndex) => {
          const key = subGroup.cables.length === 1
            ? `single_${subGroup.cables[0].originalIndex}`
            : `multi_${subGroup.cables.map(c => c.originalIndex).join('_')}_${subGroupIndex}`;
          finalGroups[key] = subGroup.cables;
        });
      });

      return finalGroups;

    } else if (listSortMode === 'gruppo') {
      // ✅ MODALITÀ GRUPPO - Raggruppa TUTTI i cavi con stesso gruppo

      const groupsByGruppo = {};
      const cablesWithoutGroup = [];

      cables.forEach((cable, index) => {
        const cableWithIndex = {
          ...cable,
          originalIndex: cable.originalIndex !== undefined ? cable.originalIndex : index,
          siglatura: settings.marcaturePerCollegamento
            ? getCableDisplayName(cable, settings.lingua, settings)
            : getEarthCableDisplayName(cable, settings.lingua)
        };

        if (cable.gruppo && cable.gruppo.trim() !== '') {
          const groupKey = cable.gruppo.trim();
          if (!groupsByGruppo[groupKey]) {
            groupsByGruppo[groupKey] = [];
          }
          groupsByGruppo[groupKey].push(cableWithIndex);
        } else {
          cablesWithoutGroup.push(cableWithIndex);
        }
      });

      const finalGroups = {};

      // Aggiungi i gruppi forzati
      Object.keys(groupsByGruppo).sort().forEach(groupName => {
        const groupCables = groupsByGruppo[groupName];
        const subGroups = analyzeAndGroupCables(groupCables, settings);

        const key = `forced_group_${groupName}`;
        finalGroups[key] = {
          type: 'forced_group',
          groupName: groupName,
          subGroups: subGroups,
          totalCables: groupCables.length,
          allCables: groupCables
        };
      });

      // Gestisci i cavi senza gruppo con logica normale
      if (cablesWithoutGroup.length > 0) {
        const normalGroups = {};

        cablesWithoutGroup.forEach(cable => {
          const groupKey = cable.siglatura || `[Cavo #${cable.originalIndex + 1}]`;
          if (!normalGroups[groupKey]) normalGroups[groupKey] = [];
          normalGroups[groupKey].push(cable);
        });

        // Applica consecutività per gruppi normali
        const allByOriginalIndex = cablesWithoutGroup.sort((a, b) => a.originalIndex - b.originalIndex);
        const finalNormalGroups = {};

        Object.entries(normalGroups).forEach(([groupKey, cablesInGroup]) => {
          if (cablesInGroup.length === 1) {
            const displayKey = `single_${cablesInGroup[0].originalIndex}`;
            finalNormalGroups[displayKey] = cablesInGroup;
            return;
          }

          // Logica consecutività
          const consecutiveSubgroups = [];
          let currentSubgroup = [cablesInGroup[0]];

          for (let i = 1; i < cablesInGroup.length; i++) {
            const prevCable = cablesInGroup[i - 1];
            const currentCable = cablesInGroup[i];

            const hasDifferentCablesBetween = allByOriginalIndex.some(cable =>
              cable.originalIndex > prevCable.originalIndex &&
              cable.originalIndex < currentCable.originalIndex &&
              (cable.siglatura || `[Cavo #${cable.originalIndex + 1}]`) !== groupKey
            );

            if (hasDifferentCablesBetween) {
              consecutiveSubgroups.push(currentSubgroup);
              currentSubgroup = [currentCable];
            } else {
              currentSubgroup.push(currentCable);
            }
          }
          consecutiveSubgroups.push(currentSubgroup);

          consecutiveSubgroups.forEach((subgroup, idx) => {
            const key = consecutiveSubgroups.length === 1
              ? `multi_${subgroup.map(c => c.originalIndex).join('_')}`
              : `multi_${subgroup.map(c => c.originalIndex).join('_')}_${idx}`;
            finalNormalGroups[key] = subgroup;
          });
        });

        // Aggiungi i gruppi normali dopo quelli forzati
        Object.entries(finalNormalGroups).forEach(([key, cables]) => {
          finalGroups[key] = cables;
        });
      }

      return finalGroups;

    } else {
      // ✅ MODALITÀ ALTRE (siglatura, colore, sezione, coloreSezione) - logica semplificata

      // Per modalità collegamento - applica sempre analyzeAndGroupCables
      if (settings.marcaturePerCollegamento) {
        const subGroups = analyzeAndGroupCables(sortedCables, settings);
        const finalGroups = {};

        subGroups.forEach((subGroup, subGroupIndex) => {
          const key = subGroup.cables.length === 1
            ? `single_${subGroup.cables[0].originalIndex}`
            : `multi_${subGroup.cables.map(c => c.originalIndex).join('_')}_${subGroupIndex}`;
          finalGroups[key] = subGroup.cables;
        });

        return finalGroups;
      }

      if (listSortMode === 'siglatura') {
        const finalGroups = {};
        Object.entries(groups).forEach(([groupKey, cablesInGroup]) => {
          // Per siglatura: nessun raggruppamento per guaina, ogni cavo è individuale
          if (cablesInGroup.length === 1) {
            finalGroups[`single_${cablesInGroup[0].originalIndex}`] = cablesInGroup;
          } else {
            finalGroups[`multi_${cablesInGroup.map(c => c.originalIndex).join('_')}`] = cablesInGroup;
          }
        });

        return finalGroups;
      }

      // Gestione per colore/sezione/coloreSezione: nessun raggruppamento per guaina
      const finalGroups = {};
      const standardCables = [];
      const multipolarCables = [];

      // Separa cavi standard e multipolari (nessun raggruppamento per guaina)
      sortedCables.forEach(cable => {
        if (cable.poli && Array.isArray(cable.poli)) {
          multipolarCables.push(cable);
        } else {
          standardCables.push(cable);
        }
      });

      // Aggiungi prima i cavi standard (ciascuno individuale)
      standardCables.forEach((cable) => {
        finalGroups[`single_${cable.originalIndex}`] = [cable];
      });

      // Aggiungi dopo i cavi multipolari (sempre in fondo)
      multipolarCables.forEach((cable) => {
        finalGroups[`multipolar_${cable.originalIndex}`] = [cable];
      });

      return finalGroups;
    }
  }, [cables, settings.lingua, settings.marcaturePerCollegamento, listSortMode, universalSort, createSortContext]);

  return { groupCablesByMode };
};